import React, { FC, memo, useState, useMemo, createRef, RefObject, useEffect } from 'react';
import { IM08BundleShopTheLookBlock, IProductRequest } from '@montana/models/generated/cms.interface';
import { Media } from '@montana/shared/media';
import { Link } from '@montana/shared/link';
import { epiMediaRatioToEnum } from '@montana/helpers/epi-mapping.helper';
import { Button, ButtonStyles } from '@montana/shared/button';
import cx from 'clsx';

import { MobileOnly, LargerThanMobile } from '@montana/shared/responsive';
import { SvgIcon } from '@montana/shared/svg-icon';
import { M08ShopTheLookHeroProduct, IM08ShopTheLookHeroProductRef } from './m08-shop-the-look-hero-product.component';
import { useCart } from '@montana/containers/cart';
import { IGtmProductWithQuantity } from '@montana/services/tracking/tracking';
import { useTracking } from '@montana/containers/tracking';
import { IFEBLock } from '@montana/models/dev';
import { Breadcrumbs } from '@montana/shared/breadcrumbs';
import { formatString } from '@montana/helpers/string-format/string-format.helper';
import { useSettings } from '@montana/containers/settings/settings.container';
import { isNotNullOrUndefined } from '@montana/helpers/type.helpers';
import { getFindDealerUrl } from '@montana/helpers/find-dealer.helper';
import { useTranslations } from '@montana/containers/translations';

export const M08ShopTheLookHeroBlock: FC<IM08BundleShopTheLookBlock & IFEBLock> = memo((props: IM08BundleShopTheLookBlock & IFEBLock) => {
    const {
        compositionImage,
        compositionPrice,
        compositionVideo,
        description,
        dispatchTime,
        disclaimer,
        link,
        mediaDisplayRatio,
        products,
        title,
        isFirstOnPage,
    } = props;

    const [mediaZoomed, setMediaZoomed] = useState(false);
    const [productRefs, setProductRefs] = useState<RefObject<IM08ShopTheLookHeroProductRef>[]>([]);
    const [findDealerUrl, setFindDealerUrl] = useState<string | undefined>();
    const { addCartLine, updateQuantityLoading } = useCart();
    const { market } = useSettings();
    const { trackAddToCart } = useTracking();
    const translate = useTranslations();

    useEffect(() => {
        if (!!products?.length) {
            setProductRefs(() => products?.map(() => createRef<IM08ShopTheLookHeroProductRef>()));
            const commonDealers: string[] = products.map(p => p.dealers).filter(isNotNullOrUndefined).reduce((a, c) => a?.concat(c), []);
            setFindDealerUrl(getFindDealerUrl(Array.from(new Set([...commonDealers]))));
        }
    }, [products])

    function onMediaClick() {
        setMediaZoomed((value: boolean) => !value);
    }

    const addComposition = () => {
        if (!!productRefs.length) {
            const productsToAdd: IProductRequest[] = productRefs.map(p => p.current?.getToAddRequest(true)!).filter(p => !!p);

            if (products?.length) {
                const gtmProducts: IGtmProductWithQuantity[] = products?.map(product => ({
                    id: product.code || '',
                    name: product.name || '',
                    price: product.price?.priceWithDiscount?.amount || 0,
                    quantity: product.amount,
                    ean: product.price?.ean
                }));

                trackAddToCart(products[0].price?.priceWithDiscount?.currency || '', gtmProducts);
            }

            addCartLine(productsToAdd);
        }
    }

    const InformationHeader = useMemo(() => (
        <div className="shop-the-look-hero__header">
            {isFirstOnPage ? <h1 className="shop-the-look-hero__title">{title}</h1> : <h3 className="shop-the-look-hero__title">{title}</h3>}
            <p className="shop-the-look-hero__description">{description}</p>
            <div className="shop-the-look-hero__links">
                {link?.map((l, i) => (
                    <Link
                        key={i}
                        to={l.url}
                        title={l.title}
                        withStroke
                        strokeActive
                    >
                        {l.name}
                    </Link>
                ))}
            </div>
        </div>
    ), [title, description, link]);

    return (
        <section className={`shop-the-look-hero shop-the-look-hero--${mediaDisplayRatio}${isFirstOnPage ? ' shop-the-look-hero--hero' : ''}`}>
            <div className="shop-the-look-hero__background"></div>
            <div className="shop-the-look-hero__content">
                {isFirstOnPage && <div className="shop-the-look-hero__breadcrumbs"><Breadcrumbs /></div>}
                <div className={`shop-the-look-hero__media-wrapper shop-the-look-hero__media-wrapper--${mediaDisplayRatio}`}>
                    <MobileOnly>
                        {InformationHeader}
                    </MobileOnly>
                    <div
                        className={cx(`shop-the-look-hero__media shop-the-look-hero__media--${mediaDisplayRatio}`, {
                            'shop-the-look-hero__media--zoomed': mediaZoomed,
                        })}
                    >
                        <button
                            className="shop-the-look-hero__media__zoom-cta"
                            onClick={onMediaClick}
                        >
                            <SvgIcon className="shop-the-look-hero__media__zoom-svg" iconName="close" />
                        </button>
                        <Media
                            image={compositionImage}
                            video={compositionVideo}
                            mediaRatio={epiMediaRatioToEnum(mediaDisplayRatio)}
                            placeholder
                            sizes={ mediaDisplayRatio === 'portrait' ? [
                                '(min-width: 1200px) 490px',
                                '(min-width: 1000px) 340px',
                                '50vw'
                            ].join(', ') : [
                                '(min-width: 1367px) 1090px',
                                '(min-width: 1000px) 800px',
                                '(min-width: 480px) 80vw',
                                '100vw'
                            ].join(', ')}
                        />
                    </div>
                </div>
                <div className={`shop-the-look-hero__information shop-the-look-hero__information--${mediaDisplayRatio}`}>
                    <LargerThanMobile>
                        {InformationHeader}
                    </LargerThanMobile>
                    <section className="shop-the-look-hero__product-list">
                        {productRefs.length === products?.length && products?.map((product, index) => (
                            <M08ShopTheLookHeroProduct product={product} key={`${product.name}_${product.code}_${index}`} ref={productRefs[index]} />
                        ))}
                    </section>
                    {!!disclaimer && <p className="shop-the-look-hero__disclaimer">{disclaimer}</p>}
                    {!market?.hidePrices && (
                        <div className="shop-the-look-hero-total">
                            <p className="shop-the-look-hero-total__price">{compositionPrice?.priceWithDiscount?.formattedWithCurrency}</p>
                            {compositionPrice?.beforePrice?.amount !== compositionPrice?.priceWithDiscount?.amount && (
                                <span className="shop-the-look-hero-total__prev-price">
                                    {translate(t => t.productPage.shopTheLookHeroPrevPrice, '[Before]')}
                                    &nbsp;
                                    <s>{compositionPrice?.beforePrice?.formattedWithCurrency}</s>
                                </span>
                            )}
                        </div>
                    )}
                    {(!!compositionPrice?.savedAmount?.amount && !market?.hidePrices) && <p className="shop-the-look-hero__discount">{!!compositionPrice?.savedAmount?.amount}</p>}
                    {(!!dispatchTime && !market?.hidePrices && !market?.disableCart) && (
                        <p className="shop-the-look-hero__dispatch-time">
                            {formatString(translate(t => t.general.dispatchTime, '[Dispatch time: {0}]'), `${dispatchTime}`)}
                        </p>
                    )}
                    <Button
                        className="shop-the-look-hero__cta"
                        style={ButtonStyles.Primary}
                        isLoading={updateQuantityLoading}
                        onClick={!market?.disableCart ? addComposition : undefined}
                        href={market?.disableCart ? findDealerUrl : undefined}
                    >
                        {(!market?.disableCart && !compositionPrice?.visibleNotBuyable) && translate(t => t.productPage.shopTheLookHeroAddComposition, '[Add to basket]')}
                        {(market?.disableCart || compositionPrice?.visibleNotBuyable) && translate(t => t.productPage.productPageFindDealer, '[Find dealer]')}
                    </Button>
                </div>
            </div>
        </section >
    );
});
